/* ============================================================
   Das Wörterbuch von P5 (SPEC §90.3, §13.7): Katalog-Übersicht, Anlegen, Editor,
   Karten-Panel, Bilder, Stände. Hängt in `lib/texte/index.ts` als eigener Ast unter
   `t.editor`; der Name des Exports ist Vertrag.

   Tonlage aus .impeccable.md: ruhig, präzise, warm. Die Oberfläche spricht niemanden
   direkt an („Stand einfrieren“, nicht „Friere den Stand ein“). Fachwörter bleiben
   Fachwörter (Stand, Abstimmung, Richtqualität, Heldenbild). Der Wortlaut folgt dem
   Canvas (Paket D) — wo der Canvas einen Satz zeigt, steht er hier wörtlich.

   Fehlersätze des Servers kommen fertig aus `detail` und stehen NICHT hier.
   ============================================================ */
import type { KartenArt, Status } from "../../api/types";

const mehrzahl = (n: number, eins: string, viele: string) => `${n} ${n === 1 ? eins : viele}`;

export const editor = {
  titel: "Kataloge, Editor, Stände",

  /* ---------------------------------------------------------- Übersicht (§13.5) */
  kataloge: {
    titel: "Kataloge",
    unterzeile: (kataloge: number, projekte: number) =>
      `${mehrzahl(kataloge, "Katalog", "Kataloge")} in ${mehrzahl(projekte, "Projekt", "Projekten")}`,
    zuletzt: (wann: string) => `zuletzt geändert ${wann}`,
    leerUnterzeile: "Hier stehen die Ausstattungskataloge aller Projekte.",
    suche: "Projekt, Nummer oder Katalog",
    neu: "Neuer Katalog",
    neuKurz: "Neu",
    leerTitel: "Noch keine Kataloge",
    leerText:
      "Ein Katalog gehört zu einem Projekt. Den ersten anlegen — die fünf Kapitel aus den Kategorien kommen auf Wunsch gleich mit.",
    muster: "Musterkatalog laden",
    musterGeladen: "Der Musterkatalog ist angelegt.",
    geloescht: "Gelöschte Kataloge",
    keineTreffer: "Kein Katalog passt zur Suche.",
    karten: "Karten",
    stand: (s: string) => `STAND ${s}`,
    letzterStand: (nr: number, wann: string) => `Stand #${nr} · ${wann}`,
    keinStand: "noch kein Stand",
    oeffnen: "Katalog öffnen",
    // Projekte ohne Katalog (§19.17-1): ein Projekt mit eingelesenem Modell, aber noch ohne
    // Katalog, war von hier aus nicht zu finden — Modell, Zuordnen und Importe nur über den Link.
    ohneKatalog: "Noch kein Katalog",
    ohneKatalogModell: "Das Modell ist eingelesen — Zuordnen geht schon, die Karten entstehen im Katalog.",
    ohneKatalogLeer: "Für dieses Projekt gibt es noch kein Modell und keinen Katalog.",
    anlegen: "Katalog anlegen",
    anlegenFuer: (projekt: string) => `Katalog anlegen für ${projekt}`,
  },

  /* ---------------------------------------------------------- Anlegen (§13.5, §3.6) */
  neu: {
    titel: "Neuer Katalog",
    projekt: "Projekt",
    vorhandene: "Vorhandene Projekte",
    vorhandeneHinweis: "Ein weiterer Katalog eines Projekts übernimmt dessen Modell und Zuordnungen.",
    vorhandeneSuche: "Projekt oder Nummer",
    keineProjekte: "Noch keine Projekte angelegt.",
    kataloge: (n: number) => mehrzahl(n, "Katalog", "Kataloge"),
    vonHand: "Neues Projekt von Hand",
    vonHandHinweis: "Projekt nicht dabei? Name und Nummer von Hand eintragen — verknüpfen geht später.",
    name: "Name",
    nummer: "Nummer",
    ort: "Ort",
    projekteId: "projekte-ID",
    projekteIdHinweis: "freiwillig — die Kennung aus projekte.lilasp.de",
    aussenSuche: "Aus projekte.lilasp.de",
    aussenPlatzhalter: "Projekt suchen …",
    aussenTreffer: (n: number) => `${n} Treffer`,
    aussenGespiegelt: "schon hier",
    stand: "Stand",
    standHinweis: (s: string) => `Erscheint im Kopf jeder Seite: „AUSSTATTUNG: STAND ${s}“.`,
    monat: "Monat",
    jahr: "Jahr",
    mitKapiteln: "Kapitel aus Kategorien anlegen",
    mitKapitelnHinweis: "Bodenbeläge · Einfassungen · Einbauten · Spielgeräte · Vegetation",
    kopieren: "Aus Katalog kopieren",
    kopierenWaehlen: "Katalog wählen …",
    kopierenHinweis: "Übernimmt Kapitel, Karten und Bilder. Ohne gewähltes Projekt bleibt es beim Projekt des Vorbilds.",
    anlegen: "Katalog anlegen",
    fehltProjekt: "Bitte ein Projekt wählen oder von Hand eintragen.",
    fehltName: "Name und Nummer des Projekts fehlen.",
    gewaehlt: "gewählt",
    abwaehlen: "Wahl aufheben",
  },

  geloescht: {
    titel: "Gelöschte Kataloge",
    unterzeile: "Weich gelöschte Kataloge lassen sich wiederherstellen. Nur für Admins.",
    leer: "Es gibt keine gelöschten Kataloge.",
    wiederherstellen: "Wiederherstellen",
    wiederhergestellt: "Der Katalog ist wiederhergestellt.",
    geloeschtAm: (wann: string) => `gelöscht ${wann}`,
  },

  /* ---------------------------------------------------------- Editor (§4.1) */
  leiste: {
    abgabeAnsicht: "Abgabe-Ansicht",
    pdf: "PDF",
    staende: "Stände",
    einfrieren: "Stand einfrieren",
    anwesend: (namen: string) => `Gerade dabei: ${namen}`,
    standChip: (s: string) => `STAND ${s}`,
    kapitelListe: "Kapitel",
    panel: "Panel",
  },

  kapitel: {
    titel: "Kapitel",
    zaehler: (k: number, n: number) => `${mehrzahl(k, "Kapitel", "Kapitel")} · ${mehrzahl(n, "Karte", "Karten")}`,
    deckblatt: "Deckblatt",
    inhalt: "Inhalt",
    seite: (nr: number) => `S. ${nr}`,
    neu: "Kapitel",
    neuTitel: "Neues Kapitel",
    karte: "Karte",
    leer: "Noch keine Kapitel.",
    neuerFassung: "Bibliothek hat neuere Fassungen",
    ziehen: "Ziehen zum Sortieren",
    aufklappen: "Karten zeigen",
    zuklappen: "Karten verbergen",
    kartenLeer: "Keine Karten in diesem Kapitel.",
    ansageAufheben: (titel: string) => `„${titel}“ aufgenommen.`,
    // Die Ansagen nennen das Ziel (§13.1: dnd-kit gerade wegen der Ansagen): beim Pfeilen
    // muss ein Screenreader sagen, WO Kapitel oder Karte landen würden.
    ansageUeber: (titel: string, ziel: string, wie: "stelle" | "anfang" | "hinter") =>
      wie === "stelle" ? `„${titel}“ an die Stelle von „${ziel}“.` : wie === "anfang" ? `„${titel}“ an den Anfang von „${ziel}“.` : `„${titel}“ hinter „${ziel}“.`,
    ansageAbgelegt: (titel: string) => `„${titel}“ abgelegt.`,
    ansageAbgelegtBei: (titel: string, ziel: string, wie: "stelle" | "anfang" | "hinter") =>
      wie === "stelle"
        ? `„${titel}“ an der Stelle von „${ziel}“ abgelegt.`
        : wie === "anfang"
          ? `„${titel}“ am Anfang von „${ziel}“ abgelegt.`
          : `„${titel}“ hinter „${ziel}“ abgelegt.`,
    ansageAbgebrochen: "Sortieren abgebrochen.",
  },

  neueKarte: {
    leer: "Leere Karte",
    artWaehlen: "Art wählen",
    bibliothek: "Aus der Bibliothek …",
    modell: "Aus dem Modell …",
    keinKapitel: "Zuerst ein Kapitel anlegen.",
    angelegt: "Karte angelegt.",
    bibliothekTitel: "Aus der Bibliothek einfügen",
    bibliothekUnterzeile: "Das Produkt wird in eine Karte kopiert. Die Herkunft bleibt gespeichert.",
    bibliothekSuche: "Produkt, Hersteller oder Kennung",
    bibliothekLeer: "Kein bestätigtes Produkt passt.",
    einfuegen: "Einfügen",
    inKapitel: "In Kapitel",
  },

  seiten: {
    seiteVon: (nr: number, n: number) => `Seite ${nr} / ${n}`,
    zoom: "Zoom",
    zoomBreite: "Breite",
    zoom100: "100 %",
    zoom200: "200 %",
    wirdGesetzt: "Seite wird gesetzt …",
    fehler: "Diese Seite konnte nicht gesetzt werden.",
    bildFehler: "Das Seitenbild ließ sich nicht laden.",
    bildErneut: "Erneut laden",
    platzhalter: (n: number) => `${mehrzahl(n, "Bild", "Bilder")} als Platzhalter · Lizenz unbekannt`,
    keineSeiten: "Der Seitenplan wird berechnet …",
    planFehler: "Der Seitenplan ließ sich nicht laden.",
    entwurf: "Entwurf",
    verschieben: "Karte verschieben",
    fokus: "Bildfokus ziehen",
    bearbeitet: (name: string) => `${name} bearbeitet diese Karte gerade.`,
    ablegenHier: "Bild hier ablegen: wird Heldenbild",
    heldErsetzenTitel: "Heldenbild ersetzen?",
    heldErsetzenText: "Die Karte hat schon ein Heldenbild. Das neue Bild tritt an die Stelle des bisherigen; das bisherige bleibt im Reiter Bilder und lässt sich dort wieder anhängen.",
    titel: "Seiten",
    heldErsetzen: "Ersetzen",
    verschoben: "Karte verschoben.",
    seiteAlt: (nr: number) => `Seite ${nr}`,
    /** Zugänglicher Name eines Kartenrahmens, dessen Karte noch kein Label hat. */
    karteOhneLabel: "Karte ohne Label",
  },

  /* ---------------------------------------------------------- Panel (§4.3) */
  panel: {
    katalog: "Katalog",
    einstellungen: "Einstellungen",
    kapitelEtikett: (nr: string) => `Kapitel ${nr}`,
    karteEtikett: (kapitel: string, n: number) => `${kapitel} · Karte ${n}`,
    gespeichert: (v: number) => `Gespeichert · v${v}`,
    speichert: "Speichert …",
    konflikt: "Nicht gespeichert — Konflikt",
    fehler: "Nicht gespeichert — noch einmal versuchen",
    nochmal: "Noch einmal",
    konfliktLoesen: "Konflikt lösen …",
    rueckgaengig: "Rückgängig",
    rueckgaengigLeer: "Nichts rückgängig zu machen",
    mehr: "Mehr",
    karteLoeschen: "Karte löschen",
    karteGeloescht: "Karte gelöscht.",
    karteWiederherstellen: "Wiederherstellen",
    karteWiederhergestellt: "Karte wiederhergestellt.",
    laedt: "Karte wird geladen …",
    weg: "Diese Karte gibt es nicht mehr.",
    fremdGeaendert: (name: string) => `${name} hat diese Karte geändert.`,
    neuLaden: "Neu laden",
    jemand: "Jemand",
    rueckUebersprungen: (felder: string, name: string) => `Rückgängig: „${felder}“ hat inzwischen ${name} geändert — das bleibt stehen.`,
    abgewiesen: "Nicht gespeichert: Diese Eingabe hat der Server abgewiesen.",
  },

  reiter: {
    inhalt: "Inhalt",
    bilder: "Bilder",
    layout: "Layout",
    status: "Status",
    herkunft: "Herkunft",
    ifc: "IFC",
  },

  katalogForm: {
    name: "Name",
    nameHinweis: "Leer = Projektname aus dem Projekt",
    monat: "Monat",
    jahr: "Jahr",
    stand: "Stand",
    titelzeile: "Titelzeile",
    untertitel: "Untertitel",
    hinweis: "Hinweis",
    partner: "Partner",
    partnerHilfe: "Mehrere Partner mit „|“ oder Zeilenumbruch trennen. Stempel und Kopfzeilen setzen sie als „A · B“, das Deckblatt je Partner eine Zeile.",
    partnerLogos: "Partnerlogos",
    partnerLogosHilfe: "Auf dem Deckblatt unten links unter „In Abstimmung mit“, in dieser Reihenfolge. Höchstens 4.",
    partnerLogosOhne: "Ohne Logos stehen dort die Partnernamen.",
    partnerLogosVoll: "Vier Partnerlogos — mehr passen nicht.",
    partnerLogoHochladen: "Partnerlogo hochladen (SVG oder PNG)",
    partnerLogoGeladen: (n: number) => (n === 1 ? "Partnerlogo hinzugefügt." : `${n} Partnerlogos hinzugefügt.`),
    partnerLogoNr: (n: number) => `Partnerlogo ${n}`,
    // Mit Nummer: jede Kachel hat dieselben drei Knöpfe, und ein Screenreader muss sagen,
    // welches Logo er verschiebt oder entfernt.
    partnerLogoNachVorn: (n: number) => `Partnerlogo ${n} weiter nach vorn`,
    partnerLogoNachHinten: (n: number) => `Partnerlogo ${n} weiter nach hinten`,
    partnerLogoEntfernen: (n: number) => `Partnerlogo ${n} entfernen`,
    partnerLogosZuViele: (n: number) => `Nur noch ${n} ${n === 1 ? "Platz" : "Plätze"} frei — die übrigen Dateien bleiben weg.`,
    stempel: "Stempeltext-Vorgabe",
    stempelEinfuegen: "{partner} einfügen",
    logo: "Logo",
    logoHochladen: "SVG oder PNG hochladen",
    hauslogo: "Hauslogo",
    hauslogoAktiv: "Es gilt das Hauslogo.",
    eigenesLogo: "Eigenes Logo",
    logoGeladen: "Logo gesetzt.",
    kopfzeilen: "Kopfzeilen",
    kopfzeile: (n: number) => `Kopfzeile ${n}`,
    kopfzeileLeer: "dritte Zeile (freiwillig)",
    kopfzeilenHilfe: "Platzhalter {name} {stand} {nummer} {partner}",
    folio: "Folio",
    tocSeitenzahlen: "Seitenzahlen im Inhalt",
    kapitelnummern: "Kapitelnummern zeigen",
    hinweisfarbe: "Hinweisfarbe",
    zuruecksetzen: "Zurücksetzen",
    textgroesse: "Textgröße",
    labelgroesse: "Labelgröße",
    pt: (n: number) => `${String(n).replace(".", ",")} pt`,
    anhangBildnachweis: "Bildnachweis-Anhang",
    anhangHersteller: "Herstellerverzeichnis",
    anhangStueckliste: "Stückliste aus dem Modell (Anhang)",
    anhangStuecklisteHilfe: "Tabelle als Anhang, aus dem aktuellen Import",
    anhangStuecklisteAus: "Geht erst, wenn das Projekt einen fertigen Import hat.",
    mengeZeigen: "Menge auf den Karten",
    loeschen: "Katalog löschen",
    loeschenFrage: "Den Katalog weich löschen? Ein Admin kann ihn unter „Gelöschte Kataloge“ wiederherstellen.",
    geloescht: "Katalog gelöscht.",
    ausgaben: "Ausgabe",
    anhaenge: "Anhänge",
    kopf: "Kopf und Deckblatt",
  },

  kapitelForm: {
    titel: "Titel",
    nummer: "Nummer",
    nummerHinweis: (n: number | null) => (n === null ? "Leer = fortlaufend" : `Leer = fortlaufend (${n})`),
    neueSeite: "Auf neuer Seite beginnen",
    kartenArt: "Vorgabe-Kartenart",
    kategorie: "Kategorie",
    kategorieHinweis: "Bestimmt, wohin Zuordnungen aus dem Modell neue Karten legen.",
    keineKategorie: "keine",
    loeschen: "Kapitel löschen",
    loeschenHinweis: "Löschen geht erst, wenn das Kapitel leer ist.",
    geloescht: "Kapitel gelöscht.",
    titelLeer: "Ein Kapitel braucht einen Titel — leer wird nichts gespeichert.",
  },

  arten: {
    standard: "Standard",
    info: "Info",
    vegetation: "Vegetation",
    lawn: "Rasen",
  } satisfies Record<KartenArt, string>,

  status: {
    werte: ["offen", "in Abstimmung", "abgestimmt", "entfallen"] as Status[],
    kurz: { offen: "offen", "in Abstimmung": "in Abst.", abgestimmt: "abgestimmt", entfallen: "entfallen" } satisfies Record<Status, string>,
  },

  /* ---------------------------------------------------------- Karte: Inhalt */
  inhalt: {
    kartenArt: "Kartenart",
    label: "Label",
    labelHilfe: "Umschalt+Enter bricht um. Großschreibung macht die Vorlage.",
    labelZeigen: "Label zeigen",
    labelZeigenHilfe: "Gleiche Labels nebeneinander zeigt die Vorlage ohnehin nur einmal.",
    titelZuerst: "Titel vor Hersteller",
    hersteller: "Hersteller",
    herstellerLink: "Herstellerlink",
    herstellerLinkPlatzhalter: "https://…",
    anreichern: "Anreichern …",
    anreichernTitel: "Aus der Herstellerseite anreichern",
    nurHttp: "Nur http- oder https-Adressen.",
    produkttitel: "Produkttitel",
    botanisch: "Botanischer Name",
    deutsch: "Deutscher Name",
    gedankenstrich: "Gedankenstrich",
    felder: "Felder",
    felderHilfe: "Schlüssel · Wert · R = rot",
    feld: "Feld",
    schluessel: "Schlüssel",
    wert: "Wert",
    rot: "Rot hervorheben",
    rotKurz: "R",
    feldLoeschen: "Feld löschen",
    gruppen: "Gruppen",
    gruppe: "Gruppe",
    gruppeUeberschrift: "Überschrift",
    gruppeLoeschen: "Gruppe löschen",
    felderZahl: (n: number) => mehrzahl(n, "Feld", "Felder"),
    text: "Text",
    textDanach: "Text danach",
    hinweis: "Hinweis",
    hinweisRot: "erscheint rot",
    beschreibung: "Beschreibung",
    zwischen: "Zwischenüberschrift",
    absatz: "Absatz",
    absatzNeu: "Absatz",
    absatzLoeschen: "Absatz löschen",
    mischungTitel: "Mischung — Titel",
    mischung: "Mischung",
    mischungZeile: "Zeile",
    mischungLoeschen: "Zeile löschen",
    neben: "Nebenprodukt",
    nebenHersteller: "Hersteller",
    nebenTitel: "Titel",
    nebenText: "Text",
    nebenBild: "Bild",
    leer: "Leer",
    ziehen: "Ziehen zum Sortieren",
  },

  /* ---------------------------------------------------------- Karte: Bilder (§4.3, §4.7) */
  bilder: {
    rollen: {
      hero: "Heldenbild",
      swatch: "Farbmuster",
      detail: "Detail",
      sketch: "Skizze",
      side: "Nebenbild",
      palette: "Palette",
      plan: "Plan",
    } as Record<string, string>,
    rolle: "Rolle",
    keine: "Noch keine Bilder auf dieser Karte.",
    fokusX: "Fokus x",
    fokusY: "Fokus y",
    fuellung: "Füllung",
    fokusHilfe: "Den Fokus kann man auch auf der Seite ziehen.",
    bildhinweis: "Bildhinweis",
    bildhinweisPlatzhalter: "Nur Beispielbild, geänderte Anordnung",
    entfernen: "Von der Karte nehmen",
    nachOben: "Nach oben",
    nachUnten: "Nach unten",
    farbauswahl: "Farbauswahl",
    farbauswahlHilfe: "Streifen mit gedrehtem Label rechts am Heldenbild",
    farbauswahlLabel: "Label",
    farbauswahlVorgabe: "Farbauswahl",
    farbauswahlBild: "Bild der Farbauswahl",
    ablegen: "Bilder hierher ziehen",
    ablegenHilfe: "JPEG, PNG, WebP, HEIC, TIFF, SVG oder PDF · bis 40 MB ·",
    waehlen: "Dateien wählen",
    laedtHoch: (p: number) => `Lädt hoch … ${p} %`,
    hochgeladen: (n: number) => (n === 1 ? "Bild hochgeladen." : `${n} Bilder hochgeladen.`),
    zuViele: "Höchstens 20 Dateien auf einmal.",
    zuGross: (name: string) => `${name} ist größer als 40 MB.`,
    wirdAufbereitet: "wird aufbereitet …",
    vorschlag: (rolle: string, fit: string) => `Vorschlag: ${rolle}, ${fit}`,
    vorschlagUebernehmen: "Übernehmen",
    vorschlagVerwerfen: "Verwerfen",
    fit: { cover: "füllend", contain: "eingepasst" } as Record<string, string>,
    bildWahl: "Bild wählen",
    keinBild: "kein Bild",
    keineBilderImKatalog: "Im Katalog gibt es noch keine Bilder.",
    rechte: "Rechte",
  },

  rechte: {
    titel: "Bildrechte",
    titelMehrere: (n: number) => `Bildrechte für ${n} Bilder`,
    unterzeile: "Lizenz, Credit und Quelle gehören zum Bild, nicht zur Karte.",
    lizenz: "Lizenz",
    lizenzen: { eigen: "eigen", erlaubt: "erlaubt", angefragt: "angefragt", unbekannt: "unbekannt" } as Record<string, string>,
    unbekanntWarnung: "Im Abgabe-PDF erscheint ein Platzhalter.",
    credit: "Credit",
    creditPlatzhalter: "© Hersteller",
    quelle: "Quelle",
    quellePlatzhalter: "https://…",
    notiz: "Notiz",
    gespeichert: "Rechte gesichert.",
    fuerAlle: "Gilt für alle gewählten Bilder.",
    vorschlag: (rolle: string, fit: string) => `Vorschlag des Bildmodells: ${rolle}, ${fit}`,
  },

  /* ---------------------------------------------------------- Karte: Layout */
  layout: {
    heldenbreite: "Heldenbreite",
    heldenbreiteHilfe: "leer = Vorgabe der Art · 40–375",
    textbreite: "Textbreite",
    textbreiteHilfe: "40–375",
    zusatzabstand: "Zusatzabstand",
    zusatzabstandHilfe: "0–60",
    textversatz: "Textversatz",
    textversatzHilfe: "−5 bis 10",
    mm: "mm",
    zeilen: "Zeilen",
    seitenumbruch: "Seitenumbruch davor",
    seitenumbruchHilfe: "Die Karte beginnt auf einer neuen Seite.",
    feldstil: "Feldstil",
    feldstilHilfe: "tabbed: Schlüssel in fester Spalte · inline: Schlüssel und Wert in einer Zeile",
    doppelpunkt: "Doppelpunkt nach dem Schlüssel",
    ausserhalb: (min: number, max: number) => `Erlaubt sind ${min} bis ${max}.`,
  },

  /* ---------------------------------------------------------- Karte: Status */
  statusForm: {
    status: "Status",
    entfallenHilfe: "„entfallen“ blendet die Karte im PDF aus; in der Kapitelliste bleibt sie durchgestrichen.",
    stempelZeigen: "Stempel zeigen",
    stempeltext: "Stempeltext",
    stempeltextHilfe: (vorgabe: string) => `Leer lassen = Vorgabe des Katalogs: „${vorgabe}“.`,
    stempelVerkleinert: (pt: number) => `Langer Text: im Katalog etwa ${String(pt).replace(".", ",")} pt statt 31,55 pt, höchstens drei Zeilen.`,
    aussparung: "Aussparung",
    aussparungHilfe: "Weiß hinterlegt, damit der Stempel über dem Bild lesbar bleibt.",
  },

  /* ---------------------------------------------------------- Karte: Herkunft (§7.2, §7.3) */
  herkunft: {
    keine: "Diese Karte stammt nicht aus der Bibliothek.",
    kopiertAus: (v: number) => `kopiert aus v${v}`,
    neuere: (v: number) => `Bibliothek hat eine neuere Fassung (v${v}).`,
    vergleichen: "Vergleichen …",
    vorschlagen: "In Bibliothek vorschlagen",
    vorschlagTitel: "In Bibliothek vorschlagen",
    vorschlagUnterzeile:
      "Der Vorschlag kopiert den Inhalt der Karte ohne Layout, Stempel und Hinweis. Ein Admin bestätigt ihn.",
    alsAenderung: (name: string) => `Als Änderung an „${name}“ vorschlagen`,
    alsAenderungJa: "Ein Admin kann den Vorschlag als neue Fassung des Produkts übernehmen oder ihn stattdessen bestätigen.",
    alsAenderungNein: "Der Vorschlag wird ein eigenes, neues Produkt.",
    kategorie: "Kategorie",
    vorgeschlagen: "Vorgeschlagen. Ein Admin prüft den Vorschlag.",
    fassungen: "Fassungen dieser Karte",
    keineFassungen: "Noch keine Fassungen.",
    aktuell: "aktuell",
    wiederherstellen: "Wiederherstellen",
    wiederhergestellt: (v: number) => `Fassung v${v} wiederhergestellt.`,
    produktLaedt: "Produkt wird geladen …",
    // Dreiwegevergleich (§7.3, Artboard HerkunftVergleich)
    vergleichTitel: (v: number) => `Fassung ${v} der Bibliothek übernehmen`,
    vergleichUnterzeile: (label: string, v: number) =>
      `Karte „${label}“ kam aus Fassung ${v}. Übernommen wird, was angehakt ist — der Rest bleibt, wie er im Projekt ist.`,
    feld: "Feld",
    basis: (v: number) => `Basis · v${v}`,
    bibliothek: (v: number) => `Bibliothek · v${v}`,
    karte: "Karte · im Projekt",
    imProjektGeaendert: "im Projekt geändert",
    danach: (v: number) =>
      `Danach gilt die Karte als auf Fassung ${v} — auch wenn nicht alles übernommen wird. Der Hinweis „neuere Fassung“ verschwindet.`,
    nichts: "Nichts übernehmen",
    uebernehmen: (n: number) => (n === 1 ? "1 Feld übernehmen" : `${n} Felder übernehmen`),
    uebernommen: "Bibliotheksfassung übernommen.",
    keineUnterschiede: "Keine Unterschiede zur Bibliothek.",
    bilderZahl: (n: number) => mehrzahl(n, "Bild", "Bilder"),
    leerWert: "—",
  },

  /* ---------------------------------------------------------- Karte: IFC (§9.7) */
  ifc: {
    menge: "Menge",
    elemente: "Elemente",
    stueck: "Stück",
    m2: "m²",
    m: "m",
    typgruppen: "Zugeordnete Typgruppen",
    keine: "Dieser Karte ist im Modell noch nichts zugeordnet.",
    imModell: "Im Modell zeigen",
    zuordnen: "Im Modell zuordnen",
    laedt: "Modell wird gelesen …",
    // §19.17-5: Menge je Typgruppe nach derselben Regel wie Zuordnen (§19.1.2).
    pflanzen: "Pflanzen",
    mengeFehlt: "Menge fehlt im Modell",
    laengeFehlt: "Länge fehlt im Modell",
    flaecheFehlt: "Fläche fehlt im Modell",
  },

  /* ---------------------------------------------------------- Konflikt (§4.8) */
  konflikt: {
    titel: (name: string) => `Inzwischen geändert von ${name}`,
    unterzeile: (name: string, label: string, wann: string, v: number) =>
      `${name} hat die Karte „${label}“ ${wann} gespeichert (v${v}). Diese Schlüssel haben beide Seiten geändert.`,
    schluessel: "Schlüssel",
    meins: "Meins (nicht gespeichert)",
    meinsKurz: "Meins",
    aktuell: (name: string, v: number) => `Aktuell · ${name}, v${v}`,
    aktuellKurz: "Aktuell",
    mehr: "mehr",
    weniger: "weniger",
    ohneFrage: "Ohne Frage zusammengeführt:",
    nurDort: (name: string) => `nur ${name}`,
    nurHier: "nur hier",
    behaltenHinweis: "„Aktuellen Stand behalten“ verwirft den ganzen lokalen Entwurf, auch die Schlüssel „nur hier“.",
    abbrechen: "Abbrechen",
    behalten: "Aktuellen Stand behalten",
    uebernehmen: "Meine Änderungen übernehmen",
    leerWert: "—",
    spalten: { "@art": "Kartenart", "@status": "Status" } as Record<string, string>,
    feldUebernommen: (feld: string) => `„${feld}“ wurde inzwischen anders gespeichert; der aktuelle Wert gilt.`,
  },

  /* ---------------------------------------------------------- Export (§5.7, §12) */
  export: {
    knopf: "Exportieren",
    titel: "Export · aktueller Katalog",
    blockHinweis: "nicht eingefroren, für Abstimmung und Weiterbearbeitung",
    modus: "Modus des Exports",
    modi: { entwurf: "Entwurf", abgabe: "Abgabe" } as Record<"entwurf" | "abgabe", string>,
    arten: { pdf: "PDF", docx: "DOCX" } as Record<"pdf" | "docx", string>,
    hilfe: (art: "pdf" | "docx", modus: "entwurf" | "abgabe") =>
      art === "pdf"
        ? modus === "abgabe"
          ? "Ungeklärte Bildrechte als Platzhalter"
          : "Alle Bilder, wie in der Vorschau"
        : modus === "abgabe"
          ? "Word, Platzhalter wie im Abgabe-PDF"
          : "Word, zum Weiterbearbeiten",
    /** §4.13: Zahl = verschiedene Bilder in den Warnungen `lizenz` des Seitenplans. */
    platzhalter: (n: number) =>
      n === 1 ? "1 Bild mit Lizenz „unbekannt“ erscheint als Platzhalter." : `${n} Bilder mit Lizenz „unbekannt“ erscheinen als Platzhalter.`,
    entwurfSatz: "Mit allen Bildern, auch ungeklärten — nicht zur Weitergabe.",
    speichertNoch: "Erst wird zu Ende gespeichert …",
    wirdErstellt: (art: string) => `${art} wird erstellt …`,
    name: (art: string, modus: string) => `${art} · ${modus}`,
    zuletzt: "Zuletzt",
    laeuft: "wird erzeugt …",
    wartet: "wartet …",
    fertig: "Fertig",
    herunterladen: "Herunterladen",
    fertigHinweis: (art: string, modus: string) => `${art} fertig (${modus})`,
    fehlgeschlagen: "Der Export ist fehlgeschlagen.",
    fehlerKurz: "fehlgeschlagen",
    erneut: "Erneut versuchen",
    hinweisSchliessen: "Hinweis schließen",
    /** Für Stand-Läufe (Einfrieren, Wiederherstellen), die über dieselbe Export-Zeile laufen. */
    fehler: "Die Ausgabe ist fehlgeschlagen.",
  },

  /* ---------------------------------------------------------- Stand einfrieren (§6.3) */
  einfrieren: {
    titel: "Stand einfrieren",
    unterzeile: "Der Stand ist danach unveränderlich: PDF, Daten und Bilder nach Inhalts-Hash.",
    titelFeld: "Titel",
    titelVorgabe: (stand: string) => `Abgabe ${stand}`,
    stand: "Stand",
    standHinweis: "Ein geänderter Stand gilt auch für den Kopf des Katalogs.",
    platzhalter: (n: number) =>
      n === 1
        ? "1 Bild erscheint als Platzhalter, weil seine Rechte ungeklärt sind."
        : `${n} Bilder erscheinen als Platzhalter, weil ihre Rechte ungeklärt sind.`,
    pruefung: (seiten: number) => `Vorabprüfung · ${mehrzahl(seiten, "Seite", "Seiten")}`,
    pruefungLaedt: "Vorabprüfung läuft …",
    lizenz: "Lizenz unbekannt",
    /** Kopf der Prüfliste: die Zahl daneben zählt Bilder (wie das Banner), hier die Karten. */
    lizenzBilder: (karten: number) => `Lizenz unbekannt · Bilder auf ${karten === 1 ? "1 Karte" : `${karten} Karten`}`,
    ppi: "Niedrige Auflösung",
    ppiWert: (ppi: number) => `${ppi} ppi`,
    ueberlauf: "Text läuft über",
    offen: "Status nicht abgestimmt",
    hinweise: "Mögliche Tippfehler",
    allesGut: "Die Vorabprüfung hat nichts gefunden.",
    blockiertNicht: "Hinweise blockieren nicht. Wer einfriert, hat die Prüfung gesehen.",
    einfrieren: "Einfrieren",
    laeuft: "Wird eingefroren …",
    fertigKurz: "Der Stand ist eingefroren.",
    pdf: "PDF herunterladen",
    zuDenStaenden: "Zu den Ständen",
    keinPlan: "Der Seitenplan fehlt noch — bitte kurz warten.",
    schliessen: "Schließen",
  },

  /* ---------------------------------------------------------- Stände (§6.4–§6.6) */
  staende: {
    titel: "Stände",
    unterzeile: (n: number) =>
      `${mehrzahl(n, "Stand", "Stände")} · Abgaben sind unveränderlich (PDF, JSON und Bilder nach Inhalts-Hash)`,
    leer: "Noch kein Stand eingefroren.",
    leerHinweis: "„Stand einfrieren“ im Editor legt die erste Abgabe an.",
    abgabe: "Abgabe",
    sicherung: "Sicherung",
    zeile: (stand: string, seiten: number, wann: string, wer: string) =>
      `STAND ${stand} · ${mehrzahl(seiten, "Seite", "Seiten")} · ${wann}${wer ? ` · ${wer}` : ""}`,
    pdf: "PDF",
    json: "JSON",
    zip: "ZIP",
    vergleichen: "Vergleichen",
    mehr: "Mehr",
    laeuft: "Wird eingefroren …",
    wiederherstellenLaeuft: "Wird wiederhergestellt …",
    detailEtikett: (nr: number, art: string) => `Stand ${nr} · ${art}`,
    /** Die Vorlagenfassung ist ein SHA-256; 64 Zeichen in der Zeile sagten niemandem etwas
     *  (Prüfung Runde 2) — wie bei Git die ersten acht. */
    detailZeile: (wann: string, wer: string, fassung: string) =>
      `${wann}${wer ? ` · ${wer}` : ""} · Vorlage ${/^[0-9a-f]{20,}$/i.test(fassung) ? fassung.slice(0, 8) : fassung}`,
    platzhalterBilder: "Lizenz unbekannt (Platzhalter)",
    niedrigeAufloesung: "Niedrige Auflösung",
    nichtAbgestimmt: "Status nicht abgestimmt",
    tippfehler: "Mögliche Tippfehler",
    seiten: "Seiten",
    pdfLaden: "PDF herunterladen",
    mitAktuell: "Mit aktuell vergleichen",
    wiederherstellen: "Wiederherstellen …",
    sicherungHinweis: "Aus einem Stand wiederherstellen legt vorher automatisch eine Sicherung des aktuellen Katalogs an.",
    waehlen: "Einen Stand wählen, um Seiten und Prüfung zu sehen.",
    seiteEntsteht: "Seite entsteht …",
    // Wiederherstellen (§6.6)
    whTitel: (nr: number) => `Aus Stand #${nr} wiederherstellen`,
    whUnterzeile: "Vorher entsteht automatisch eine Sicherung des aktuellen Katalogs.",
    whGanz: "Ganzer Katalog",
    whGanzHinweis: "Katalogfelder, Kapitel und Karten auf den Stand bringen.",
    whKarten: "Einzelne Karten",
    whKartenHinweis: "Nur die gewählten Karten auf ihren Inhalt im Stand bringen.",
    whKeineKarten: "Seit diesem Stand hat sich an keiner Karte etwas geändert.",
    whStarten: "Wiederherstellen",
    whFertig: "Wiederhergestellt. Die Sicherung steht in der Liste.",
    zurueck: "Zum Katalog",
  },

  vergleich: {
    /** Pfade des Vergleichs (fach/vergleich.py) als Wörter; ein unbekannter bleibt wörtlich.
     *  „fields:reihenfolge" stand roh in der Liste (Prüfung Runde 1). */
    pfadNamen: {
      "fields:reihenfolge": "Reihenfolge der Felder",
      "groups:reihenfolge": "Reihenfolge der Gruppen",
      label: "Label",
      title: "Produkttitel",
      manufacturer: "Hersteller",
      source_url: "Herstellerlink",
      botanical: "Botanischer Name",
      common: "Deutscher Name",
      text: "Text",
      text_after: "Text danach",
      note: "Hinweis",
      mix_title: "Mischung — Titel",
      mix: "Mischung",
      "stamp.show": "Stempel",
      "stamp.text": "Stempeltext",
      "layout.hero_w": "Heldenbreite",
      "layout.text_w": "Textbreite",
      "layout.extra_gap": "Zusatzabstand",
      "layout.text_offset_lines": "Textversatz",
      images: "Bilder",
      extra_images: "Weitere Bilder",
    } as Record<string, string>,
    feldPfad: (schluessel: string) => `Feld „${schluessel}“`,
    titel: "Vergleich",
    zuDenStaenden: "Zu den Ständen",
    standFehlt: "Diesen Stand gibt es nicht (mehr).",
    aktuell: (v: number) => `aktuell · v${v}`,
    aktuellKurz: "aktuell",
    standChip: (nr: number, titel: string) => `#${nr} ${titel}`,
    nurGeaenderte: "Nur Seiten mit Änderungen",
    katalog: "Katalog",
    kapitel: "Kapitel",
    kapitelNeu: "Neu",
    kapitelEntfernt: "Entfernt",
    kapitelUmbenannt: "Umbenannt",
    umsortiert: "Kapitel umsortiert",
    kartenGeaendert: "Karten geändert",
    kartenNeu: "Karten neu",
    kartenEntfernt: "Karten entfernt",
    verschoben: "Verschoben",
    seitenZahl: (l: number, r: number) => `${l} → ${r} Seiten`,
    seite: (nr: number) => `S. ${nr}`,
    seiteVonNach: (l: number, r: number) => `S. ${l} → S. ${r}`,
    bilderAenderung: (neu: number, entfernt: number, ersetzt: number) =>
      [neu ? `${neu} neu` : "", entfernt ? `${entfernt} entfernt` : "", ersetzt ? `${ersetzt} ersetzt` : ""]
        .filter(Boolean)
        .join(" · ") + " (Bilder)",
    statusWechsel: (l: string, r: string) => `Status ${l} → ${r}`,
    keineUnterschiede: "Keine Unterschiede.",
    seiteFehlt: "Seite gibt es hier nicht",
    links: (nr: number) => `Stand #${nr}`,
    rechts: "aktuell",
    feldunterschiede: "Feldunterschiede",
    pfad: "Pfad",
    leer: "—",
    laedt: "Vergleich wird berechnet …",
  },

  /* ---------------------------------------------------------- Telefon (§4.10) */
  mobil: {
    kapitelSprung: "Zu Kapitel springen",
    wischen: "Wischen blättert · Karte antippen",
    status: "Status",
    hinweis: "Hinweis",
    nurLesen: "Nur lesen",
    grossBildschirm: "Bearbeiten am großen Bildschirm",
    schliessen: "Schließen",
    seite: (nr: number, n: number) => `${nr} / ${n}`,
  },

  allgemein: {
    entfernen: "Entfernen",
    hinzufuegen: "Hinzufügen",
    laedt: "Wird geladen …",
    leer: "—",
    fehler: "Das hat nicht geklappt.",
  },
} as const;
